import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FieldErrors, useFormContext } from 'react-hook-form'
import { AuftragCheckbox, BefehlFormAuftrag, BefehlFormField } from '../Generic'

import { Befehl26FormData } from './schema'
import {
  BetriebsstelleField,
  BetriebsstellenartField,
  Input,
  RadioChipGroup,
  RadioChipOption
} from '../../form'
import { Betriebsstellenart } from '../../../types'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag26_10FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings: boolean
  errors?: FieldErrors
}

export default function Auftrag26_10Form({
  readOnly,
  'data-testid': testId,
  errors,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag26_10FormProps>) {
  const { control, formState, watch, setValue } =
    useFormContext<Befehl26FormData>()

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const { t } = useTranslation('ui-befehle')

  const auftrag_26_11_enabled = watch('auftrag_26_11.enabled')
  const auftrag_26_12_enabled = watch('auftrag_26_12.enabled')
  const betriebsstellenartSelection = watch('auftrag_26_10.field_26_15.art')

  useEffect(() => {
    if (
      betriebsstellenartSelection === 'Bf' ||
      betriebsstellenartSelection === 'Bft'
    ) {
      setValue('auftrag_26_10.des_der', 'des')
    } else if (
      betriebsstellenartSelection === 'Abzw' ||
      betriebsstellenartSelection === 'Üst'
    ) {
      setValue('auftrag_26_10.des_der', 'der')
    } else {
      setValue('auftrag_26_10.des_der', '')
    }
  }, [betriebsstellenartSelection, setValue])

  useEffect(() => {
    if (auftrag_26_11_enabled) {
      setValue('auftrag_26_12.enabled', false)
    }
  }, [auftrag_26_11_enabled, setValue])

  useEffect(() => {
    if (auftrag_26_12_enabled) {
      setValue('auftrag_26_11.enabled', false)
    } else {
      setValue('auftrag_26_12.field_26_13_signal', '')
    }
  }, [auftrag_26_12_enabled, setValue])

  return (
    <BefehlFormAuftrag
      auftragKennung="26.10"
      fieldName="auftrag_26_10.enabled"
      testId={testId}
      readOnly={true}
    >
      <div className="flex flex-wrap gap-2 items-baseline">
        <span data-testid={`${testId}-text`}>{t('auftraege.26_10.label')}</span>

        <div className="flex flex-wrap gap-2 items-baseline">
          {/* Group 26.11 checkbox + text */}
          {(!readOnly || auftrag_26_11_enabled) && (
            <div className="flex items-baseline">
              <BefehlFormField
                name="auftrag_26_11.enabled"
                control={control}
                label=""
                readOnly={readOnly}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-auftrag-26-11`}
                render={({ field }) => (
                  <div className="flex items-baseline gap-0.5">
                    <div className="flex justify-center items-start pt-0.25">
                      <AuftragCheckbox
                        checked={field.value}
                        label="26.11"
                        onChange={(e) => field.onChange(e.target.checked)}
                        readOnly={readOnly}
                        data-testid={`${testId}-auftrag-26-11-inner`}
                        errors={fieldErrors}
                      />
                    </div>
                    <span data-testid={`${testId}-auftrag-26-11-text`}>
                      {t('auftraege.26_10.26_11_label')}
                    </span>
                  </div>
                )}
              />
            </div>
          )}

          {/* "oder" separator */}
          {!readOnly && (
            <span data-testid={`${testId}-text-oder`}>
              {t('auftraege.26_10.oder')}
            </span>
          )}

          {/* Group 26.12 checkbox + text + field 26.13 */}
          {(!readOnly || auftrag_26_12_enabled) && (
            <div className="flex flex-wrap items-baseline gap-1">
              <BefehlFormField
                name="auftrag_26_12.enabled"
                control={control}
                label=""
                readOnly={readOnly}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-auftrag-26-12`}
                render={({ field }) => (
                  <div className="flex items-baseline gap-0.5">
                    <div className="flex justify-center items-start pt-0.25">
                      <AuftragCheckbox
                        checked={field.value}
                        label="26.12"
                        onChange={(e) => field.onChange(e.target.checked)}
                        readOnly={readOnly}
                        data-testid={`${testId}-auftrag-26-12-inner`}
                        errors={fieldErrors}
                      />
                    </div>
                    <span data-testid={`${testId}-auftrag-26-12-text`}>
                      {t('auftraege.26_10.26_12_label')}
                    </span>
                  </div>
                )}
              />

              <BefehlFormField
                name="auftrag_26_12.field_26_13_signal"
                control={control}
                className="min-w-[150px]"
                label={t('auftraege.26_10.26_13_signal_label')}
                readOnly={readOnly}
                useTextDisplayInReadOnly={false}
                disabled={!auftrag_26_12_enabled}
                activateEmptyWarning={
                  activateEmptyFieldWarnings && auftrag_26_12_enabled
                }
                data-testid={`${testId}-feld-26-13`}
                render={({ field: signalField, isDisabled, isReadOnly }) => (
                  <div>
                    <Input
                      {...signalField}
                      label={t('auftraege.26_10.26_13_signal_label')}
                      disabled={isDisabled}
                      readOnly={isReadOnly}
                      activateEmptyWarning={
                        activateEmptyFieldWarnings && auftrag_26_12_enabled
                      }
                      errors={fieldErrors}
                      data-testid={`${testId}-feld-26-13-inner`}
                    />
                    <span
                      className={
                        auftrag_26_12_enabled ? '' : 'text-gray-500 opacity-60'
                      }
                      data-testid={`${testId}-feld-26-13-label`}
                    >
                      {t('auftraege.26_10.26_13_signal_bez')}
                    </span>
                  </div>
                )}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 items-baseline">
        <span data-testid={`${testId}-text-in`}>{t('auftraege.26_10.in')}</span>
        <BefehlFormField
          name="auftrag_26_10.field_26_14_km"
          className="min-w-[150px]"
          control={control}
          label={t('auftraege.26_10.26_14_km_label')}
          readOnly={readOnly}
          useTextDisplayInReadOnly={false}
          activateEmptyWarning={activateEmptyFieldWarnings}
          data-testid={`${testId}-feld-26-14`}
          render={({ field: signalField, isDisabled, isReadOnly }) => (
            <div>
              <Input
                {...signalField}
                label={t('auftraege.26_10.26_14_km_label')}
                readOnly={isReadOnly}
                disabled={isDisabled}
                errors={fieldErrors}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-feld-26-14-inner`}
              />
              <span data-testid={`${testId}-feld-26-14-label`}>
                {t('auftraege.26_10.26_14_km_bez')}
              </span>
            </div>
          )}
        />
      </div>
      <div className="flex flex-wrap gap-1 items-baseline">
        <div className="flex flex-row items-baseline gap-1 flex-shrink-0">
          <RadioChipGroup
            value={determineDesOrDerFromBetriebsstellenart(
              betriebsstellenartSelection
            )}
            options={
              [
                { value: 'des', label: t('auftraege.26_10.des') },
                { value: 'der', label: t('auftraege.26_10.der') }
              ] as RadioChipOption[]
            }
            variant="plain"
            showOnlySelected={readOnly}
            testId={`${testId}-text-des-der`}
          />
        </div>

        <BorderedBox
          className="w-full"
          data-testid={`${testId}-feld-26-15-bordered-box`}
          footer={
            <span data-testid={`${testId}-feld-26-15-label`}>
              {t('auftraege.26_10.26_15')}
            </span>
          }
        >
          <ZugmeldestelleWrapper
            betriebsstellenartField={
              <BefehlFormField
                name={`auftrag_26_10.field_26_15.art`}
                control={control}
                label={t('auftraege.26_10.betriebsstellenart_label')}
                readOnly={readOnly}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-feld-26-15-art`}
                render={({ field: field_art }) => (
                  <BetriebsstellenartField
                    {...field_art}
                    label={t('auftraege.26_10.betriebsstellenart_label')}
                    variant="solid"
                    className="w-full"
                    errors={fieldErrors}
                    data-testid={`${testId}-feld-26-15-art-inner`}
                    selectClassName="w-full"
                  />
                )}
              />
            }
            bestriebsstelleField={
              <BefehlFormField
                name={`auftrag_26_10.field_26_15.name`}
                control={control}
                label={t('auftraege.26_10.zugmeldestelle_label')}
                readOnly={readOnly}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-feld-26-15-name`}
                render={({ field: field_name }) => (
                  <BetriebsstelleField
                    {...field_name}
                    label={t('auftraege.26_10.zugmeldestelle_label')}
                    variant="solid"
                    className="w-full"
                    errors={fieldErrors}
                    data-testid={`${testId}-feld-26-15-name-inner`}
                    typ={betriebsstellenartSelection}
                  />
                )}
              />
            }
          />
        </BorderedBox>
      </div>
    </BefehlFormAuftrag>
  )
}

function determineDesOrDerFromBetriebsstellenart(
  betriebsstellenartSelection: '' | Betriebsstellenart
) {
  if (
    betriebsstellenartSelection === Betriebsstellenart.Bft ||
    betriebsstellenartSelection === Betriebsstellenart.Bf
  )
    return 'des'
  else if (
    betriebsstellenartSelection === Betriebsstellenart.Abzw ||
    betriebsstellenartSelection === Betriebsstellenart.Uest
  )
    return 'der'
  else return ''
}
